/**
 * ElevationProfilePanel — minimal scaffold of the §7.3 elevation-profile
 * surface for the Terrain ToolCard.
 *
 * Background: the masterplan §3.4 promises the full `ProfileCanvas`
 * primitive is already implemented at `components/profile/*`, but that
 * canvas is a workspace-scale component (light technical canvas with its
 * own toolbar, station list, etc.). For a right-rail ToolCard summary we
 * just need:
 *
 *   - a tokenized empty state (no profile drawn yet),
 *   - a "Pick start / end" CTA,
 *   - a sample-count + station readout (mono) when the profile exists.
 *
 * The full `ProfileCanvas` opens in a drawer when the user clicks
 * `onOpenFullProfile`. This component is therefore a scaffold; the chart
 * area itself is intentionally a placeholder so we do not pull in a
 * charting library for WP-3.
 */

import { Callout, Chip, Icon, tokens } from '@/design';
import { useViewerSlice } from '@/state/useViewerSlice';

export interface ElevationProfileSample {
  readonly station: number;
  readonly elevation: number;
}

export interface ElevationProfilePanelProps {
  /** Picked samples; rendered as count + min/max readout. */
  samples?: readonly ElevationProfileSample[];
  /** Callback to start picking a new start/end pair on the map. */
  onPickProfile?: () => void;
  /** Callback to open the full `ProfileCanvas` workspace drawer. */
  onOpenFullProfile?: () => void;
  /** Read-only / permission state. */
  readOnly?: boolean;
  'data-testid'?: string;
}

function formatElevation(v: number): string {
  return `${v.toFixed(2)} m`;
}

export function ElevationProfilePanel({
  samples,
  onPickProfile,
  onOpenFullProfile,
  readOnly,
  'data-testid': testId,
}: ElevationProfilePanelProps) {
  const verticalExaggeration = useViewerSlice(
    (s) => s.terrainToolCard.verticalExaggeration,
  );

  const count = samples?.length ?? 0;
  const minElev = count
    ? Math.min(...samples!.map((s) => s.elevation))
    : null;
  const maxElev = count
    ? Math.max(...samples!.map((s) => s.elevation))
    : null;

  return (
    <div
      data-testid={testId ?? 'terrain-elevation-profile'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
      }}
    >
      {count === 0 ? (
        <Callout severity="info" icon="info">
          No elevation profile sampled yet. Pick a start and end point on
          the map to draw a profile.
        </Callout>
      ) : (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[2],
          }}
        >
          <Chip
            icon="query_stats"
            variant="neutral"
            label={`${count} samples`}
          />
          <Chip
            variant="neutral"
            label={`min ${formatElevation(minElev!)}`}
          />
          <Chip
            variant="neutral"
            label={`max ${formatElevation(maxElev!)}`}
          />
          <Chip
            variant="neutral"
            label={`exaggeration ${verticalExaggeration.toFixed(1)}×`}
          />
        </div>
      )}
      <div
        style={{
          display: 'flex',
          gap: tokens.space[2],
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={onPickProfile}
          disabled={readOnly || !onPickProfile}
          aria-label="Pick start and end of an elevation profile"
          data-testid="terrain-pick-profile"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            minHeight: tokens.density.touchMin,
            padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: `${tokens.border.width.thin}px solid transparent`,
            borderRadius: tokens.radius.input,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.body.size,
            fontWeight: tokens.font.weight.medium,
            cursor: !readOnly && onPickProfile ? 'pointer' : 'default',
            opacity: !readOnly && onPickProfile ? 1 : 0.5,
          }}
        >
          <Icon name="timeline" size={20} decorative />
          Pick profile
        </button>
        <button
          type="button"
          onClick={onOpenFullProfile}
          disabled={!onOpenFullProfile || count === 0}
          aria-label="Open elevation profile workspace"
          data-testid="terrain-open-profile-workspace"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            minHeight: tokens.density.touchMin,
            padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            borderRadius: tokens.radius.input,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.body.size,
            cursor:
              onOpenFullProfile && count > 0 ? 'pointer' : 'default',
            opacity: onOpenFullProfile && count > 0 ? 1 : 0.5,
          }}
        >
          <Icon name="open_in_new" size={20} decorative />
          Full profile
        </button>
      </div>
    </div>
  );
}
